import { useMutation, useQueryClient } from '@tanstack/react-query';
import { financialInstrumentQueryKey } from './keys';
import { normalizeFinancialInstrument } from '../types';
import type {
  FinancialInstrumentDto,
  FinancialInstrumentHttpRequest,
  RawFinancialInstrumentDto,
  UpdateFinancialInstrumentPayload,
} from '../types';

export interface UseUpdateFinancialInstrumentParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/financial-instrument`); se le añade `/{fiId}`. */
  financialInstrumentBaseEndpoint: string;
  httpRequest: FinancialInstrumentHttpRequest;
}

export interface UpdateFinancialInstrumentInput {
  financialInstrumentId: string;
  payload: UpdateFinancialInstrumentPayload;
}

/**
 * Modifica el Instrumento Financiero (`PUT /{id}/financial-instrument/{fiId}`) →
 * 200 + DTO. `rowVersion` viaja en el payload (concurrencia optimista); un 409 →
 * el panel surfacea y recarga. Cambiar el tipo sin permiso → 422 (FR-84).
 *
 * - `onSuccess`: reemplaza el IF en el cache (rowVersion incrementado).
 * - `onSettled`: invalida `['financial-instrument', documentId]`.
 */
export function useUpdateFinancialInstrument({
  documentId,
  financialInstrumentBaseEndpoint,
  httpRequest,
}: UseUpdateFinancialInstrumentParams) {
  const queryClient = useQueryClient();
  const key = financialInstrumentQueryKey(documentId);

  return useMutation<FinancialInstrumentDto, Error, UpdateFinancialInstrumentInput>({
    mutationFn: async ({ financialInstrumentId, payload }) => {
      const res = await httpRequest<RawFinancialInstrumentDto>(
        'PUT',
        `${financialInstrumentBaseEndpoint}/${financialInstrumentId}`,
        payload,
      );
      return normalizeFinancialInstrument(res);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<FinancialInstrumentDto | null>(key, updated);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
